import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useRef, useState, useEffect, type KeyboardEvent, type ClipboardEvent } from "react";
import Button from "./ui/button";

const otpSchema = z.object({
	code: z.string().length(6, "Please enter 6-digit code").regex(/^\d+$/, "Only numbers allowed"),
});

type OtpFormData = z.infer<typeof otpSchema>;

interface OtpFormProps {
	email: string;
	onSubmit: (code: string) => void;
	onBack?: () => void;
	onResend?: () => void;
	isLoading?: boolean;
	error?: string | null;
	info?: string | null;
}

const RESEND_COOLDOWN = 30; // seconds

export default function OtpForm({ email, onSubmit, onBack, onResend, isLoading = false, error, info }: OtpFormProps) {
	const [otp, setOtp] = useState<string[]>(["", "", "", "", "", ""]);
	const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
	const [resendCooldown, setResendCooldown] = useState(0);

	useEffect(() => {
		if (resendCooldown > 0) {
			const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
			return () => clearTimeout(timer);
		}
	}, [resendCooldown]);

	const handleResend = () => {
		if (resendCooldown > 0 || isLoading) return;
		onResend?.();
		setResendCooldown(RESEND_COOLDOWN);
	};

	const {
		handleSubmit,
		formState: { errors },
		setValue,
		trigger,
	} = useForm<OtpFormData>({
		resolver: zodResolver(otpSchema),
		defaultValues: {
			code: "",
		},
	});

	const handleChange = (index: number, value: string) => {
		// Only allow single digit
		if (value.length > 1) return;

		// Only allow numbers
		if (value && !/^\d$/.test(value)) return;

		const newOtp = [...otp];
		newOtp[index] = value;
		setOtp(newOtp);

		// Update form value
		const code = newOtp.join("");
		setValue("code", code);

		// Auto-focus next input
		if (value && index < 5) {
			inputRefs.current[index + 1]?.focus();
		}

		// Trigger validation
		trigger("code");
	};

	const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
		if (e.key === "Backspace" && !otp[index] && index > 0) {
			inputRefs.current[index - 1]?.focus();
		}
	};

	const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
		e.preventDefault();
		const pastedData = e.clipboardData.getData("text").trim();

		// Only allow 6-digit numbers
		if (!/^\d{6}$/.test(pastedData)) return;

		const newOtp = pastedData.split("");
		setOtp(newOtp);
		setValue("code", pastedData);
		trigger("code");

		// Focus last input
		inputRefs.current[5]?.focus();
	};

	const onFormSubmit = () => {
		const code = otp.join("");
		onSubmit(code);
	};

	return (
		<>
			<img className="mb-8 w-16" src="/images/sayaboutus-logomark-light.svg" alt="Say About Us" />
			<h1 className="text-xl font-semibold text-primary -leading-[1%]">Check your email</h1>
			<p className="text-sm mt-3 text-tertiary font-medium mb-6">
				We've sent a 6-digit verification code to <span className="font-semibold">{email}</span>
				{onBack && (
					<>
						{" · "}
						<button type="button" onClick={onBack} className="text-primary hover:underline font-medium">
							Change
						</button>
					</>
				)}
			</p>
			{info && (
				<div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
					<p className="text-sm text-tertiary">{info}</p>
				</div>
			)}
			<form onSubmit={handleSubmit(onFormSubmit)}>
				<div className="space-y-4">
					<div>
						<label className="block text-md font-medium text-primary mb-3">Verification Code</label>
						<div className="flex gap-2 justify-between">
							{otp.map((digit, index) => (
								<input
									key={index}
									ref={(el) => {
										inputRefs.current[index] = el;
									}}
									type="text"
									inputMode="numeric"
									maxLength={1}
									value={digit}
									onChange={(e) => handleChange(index, e.target.value)}
									onKeyDown={(e) => handleKeyDown(index, e)}
									onPaste={index === 0 ? handlePaste : undefined}
									disabled={isLoading}
									className="w-12 h-12 text-center text-lg font-semibold border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary disabled:opacity-50 disabled:cursor-not-allowed bg-white"
									autoFocus={index === 0}
								/>
							))}
						</div>
						{errors.code && <p className="text-sm text-red-500 mt-2">{errors.code.message}</p>}
					</div>
					{error && <p className="text-sm text-red-500">{error}</p>}
					<div className="pt-4">
						<Button type="submit" disabled={isLoading || otp.join("").length !== 6} className="w-full">
							{isLoading ? "Verifying..." : "Verify Code"}
						</Button>
					</div>
					{onResend && (
						<p className="text-sm text-tertiary text-center pt-2">
							Didn't receive it?{" "}
							{resendCooldown > 0 ? (
								<span className="text-gray-400">Resend in {resendCooldown}s</span>
							) : (
								<button type="button" onClick={handleResend} disabled={isLoading} className="text-primary hover:underline font-medium disabled:opacity-50">
									Resend code
								</button>
							)}
						</p>
					)}
				</div>
			</form>
		</>
	);
}
